import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { serviceURLs } from '../../environment-config'
import AddressQualification from '../components/Form/AddressQualification'
import getFullStateName from '../utils/getFullStateName'
import {
  clearCacheUserLocation,
  getCacheUserLocation,
  setCacheUserLocation,
} from '../utils/userCacheLocation'

export const UserContext = createContext()
export const useUser = () => useContext(UserContext)

function makeUserLocation(data = {}, fetchResponse = {}, geoLookup = false) {
  return {
    geoLookup,
    error: false,
    mapiResponse: fetchResponse,
    address: data.address || null,
    zipCode: data.zip_code || null,
    city: data.city_mixed_case || null,
    state: {
      fullName: data.state_full_name || null,
      abbreviation: data.state || null,
    },
  }
}

/** Gets the zip code based on geo location */
async function getGeoZipCode() {
  const result = await fetch('https://m.clear.link/geoip')
    .then((res) => (res?.ok ? res.json() : null))
    .catch(() => null)

  const validZip = result?.data?.cityPostalCode.length === 5
  return validZip ? result?.data?.cityPostalCode : null
}

/**
 * Get the users location by zip code.
 * If no zipParam, zipUrlParam, or affUrlParam is provided
 * it will get the address based on the users zip code.
 */
async function getUserLocByZip(zipParam) {
  let location = makeUserLocation()
  const zipUrlParam = new URLSearchParams(window.location.search).get('zip')
  const affUrlParam = new URLSearchParams(window.location.search).get(
    'aff_unique3'
  )

  /** Set zip based on param priority */
  let zip = zipParam || affUrlParam || zipUrlParam

  /** If we don't have a zip code, but we do have the cached location */
  const cache = getCacheUserLocation()
  const cacheExpt =
    !cache.expiration || cache.expiration <= new Date().getTime()

  if (
    !zip &&
    !cacheExpt &&
    cache.zipCode &&
    cache.state?.fullName &&
    cache.city
  ) {
    return cache
  }

  /** Get zip code by geo location if no cached location */
  let geoLookup = false
  if (!zip) {
    zip = await getGeoZipCode()
    geoLookup = true
  }

  /** If valid zip, lookup location based on zip code */
  if (zip) {
    let apiRes = {}
    await fetch(`${serviceURLs.mapiZipURL}${zip}`)
      .then((res) => {
        apiRes = res
        return res
      })
      .then((res) => (res?.ok ? res.json() : {}))
      .then((resData) => {
        const data = resData?.data?.zipCodes?.find(
          (entry) => entry.primary_record === 'P'
        )
        location = makeUserLocation(data, apiRes, geoLookup)
        setCacheUserLocation(location)
      })
      .catch((err) => {
        location.error = err
      })
  }

  return location
}

/**
 * @param {Object} props
 * @param {Object} props.address
 * @param {string} props.smartyStreetsWebsiteKey
 * @param {string} props.addressQualificationCode,
 */
async function getAddressQualificaiton({
  address,
  smartyStreetsWebsiteKey,
  addressQualificationCode,
}) {
  const addressQual = new AddressQualification({
    addressQualificationCode,
    smartyStreetsWebsiteKey,
  })
  const requestId = await window?.mapiUtils?.getRequestId()
  if (!requestId) return null
  return addressQual.checkForService(address, requestId).catch((err) => err)
}

export function UserProvider({ children }) {
  const [userLocation, setUserLocation] = useState(makeUserLocation())
  const [isLoading, setIsLoading] = useState(false)
  const [userData, setUserData] = useState(null)
  const [serviceData, setServiceData] = useState(null)
  const isClient = typeof window !== 'undefined'

  // TODO: Confirm that we no longer use this so we can remove it
  const setAddrByQualLookup = useCallback(
    (params) => {
      setIsLoading(true)
      getAddressQualificaiton(params)
        .then((result) => {
          setServiceData(result.serviceData)
          setUserLocation({
            geoLookup: false,
            address: params.address || null,
            zipCode: result?.address?.postal_code || null,
            city: result?.address?.locality || null,
            state: {
              fullName: getFullStateName(result?.address?.administrative_area),
              abbreviation: result.address.administrative_area,
            },
          })
        })
        .finally(() => setIsLoading(false))
    },
    [setIsLoading, setUserLocation]
  )

  const setUserLocByZip = useCallback(
    async (zip) => {
      setIsLoading(true)
      let res = null
      await getUserLocByZip(zip)
        .then((loc) => {
          res = loc
          setUserLocation(loc)
        })
        .finally(() => setIsLoading(false))
      return res
    },
    [setIsLoading, setUserLocation]
  )

  const clearUserLocation = useCallback(() => {
    clearCacheUserLocation()
    setUserLocation(makeUserLocation())
  }, [setUserLocation])

  useEffect(() => {
    if (isClient) setUserLocByZip()
  }, [isClient, setUserLocByZip])

  return (
    <UserContext.Provider
      value={{
        isLoading,
        userData,
        setUserData,
        serviceData,
        userLocation,
        clearUserLocation,
        setAddressLocation: setAddrByQualLookup,
        setZipLocation: setUserLocByZip,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
